import { AppContext } from "App";

import { OWDataType } from "@multiweb/types/contracts";
import { IFrameContext } from "@multiweb/types/multiweb";
import { IHasRights, IItemRightsCollection } from "Global/Base/Rights/RightTypes";
import { setupBem } from "Global/Dom/bem";
import { CultureInfo } from "Global/Dom/CultureInfo";
import { Localization } from "OW.Remoting/Remoting/Localization";
import { createElement } from "UI/UI.ElementarControls/Utils/CustomElementUtils";
import { IGenericControl, IHTMLElementWithControl, IInfoProvidingControl } from "../Controls/ControlInterfaces";
import { IContentBuilder } from "../DataBuilders/IContentBuilder";
import { MessageWindow } from "../MessageWindow";

export class ControlInfo {
    /** Získá a zobrazí data o prvku v MessageWindow */
    public static async displayInfo(clickedElement: HTMLElement, initialInfo: IControlInfoObject | null): Promise<void> {
        let element = clickedElement;
        let resultInfo: IControlInfoObject = initialInfo ?? {} as IControlInfoObject;

        if (initialInfo)
            resultInfo = initialInfo;

        let info: Partial<IControlInfoObject> = {
            language: CultureInfo.getCulture(),
            ...resultInfo,
        };
        let clickedControl: IGenericControl;
        while (element) {
            if (element instanceof IHTMLElementWithControl && element.tscControl && element.tscControl instanceof IInfoProvidingControl) {
                if (clickedControl === undefined)
                    clickedControl = element.tscControl;
                if ((element.tscControl && element.tscControl instanceof IInfoProvidingControl))
                    info = await element.tscControl.getControlInfo(info, clickedControl);
            } else if (element instanceof IInfoProvidingControl) {
                clickedControl = null;
                info = await element.getControlInfo(info, clickedControl);
            }

            if (info.className) // už je vyplněno vše, co je potřeba
                break;
            element = element.parentElement as HTMLElement;
        }

        resultInfo = { ...resultInfo, ...info };

        if (resultInfo.locKey) // Každá podporovaná komponenta obsahuje locKey, pokud tedy není vyplněno, nic nezobrazovat
            MessageWindow.showAlert(new ControlInfoMessageBuilder(resultInfo), "GUI-702479"); // Informace o komponentě
    }
}
const bemClass = setupBem("control-info");

//** Builder obsahu MessageWindow pro ControlInfo */
export class ControlInfoMessageBuilder implements IContentBuilder {
    private _controlInfo: IControlInfoObject;
    private _element: HTMLElement = createElement({ type: "div", classes: bemClass() });

    constructor(controlInfo: IControlInfoObject) {
        this._controlInfo = controlInfo;
    }

    public async build(): Promise<HTMLElement> {
        const loc = await Localization.getTexts("GUI-354669", "GUI-302180", "GUI-354852", "GUI-302176", "GUI-339923");

        const mainInfo = createElement({ type: "table", classes: bemClass.element("main-info") });

        this._addRow(loc.get("GUI-354669"), this._controlInfo.className, bemClass.element("class-name-row"), mainInfo); // Název třídy
        if (this._controlInfo.attributeName)
            this._addRow(loc.get("GUI-302180"), this._controlInfo.attributeName, bemClass.element("attr-name-row"), mainInfo); // Název atributu
        if (this._controlInfo.dataType) {
            this._addRow(
                loc.get("GUI-354852"),
                this._controlInfo.additionalTypeName
                    ? this._controlInfo.dataType + "|" + this._controlInfo.additionalTypeName
                    : this._controlInfo.dataType,
                bemClass.element("data-type-row"),
                mainInfo,
            ); // Datový typ atributu
        }
        this._addRow(loc.get("GUI-302176"), this._controlInfo.locKey, bemClass.element("lockey-row"), mainInfo); // Lokalizační klíč
        this._addRow(loc.get("GUI-339923"), this._controlInfo.language, bemClass.element("language-row"), mainInfo); // Aktuální jazyk

        this._element.appendChild(mainInfo);

        // Dodatečné info ve vývojářském módu
        if (AppContext.instance.configuration.developmentMode) {
            if (this._controlInfo.additionalInfo) {
                const additionalInfo = createElement({ type: "div", classes: bemClass.element("additional-info") });
                additionalInfo.appendChild(createElement({ type: "h5", html: "Additional info" }));
                let text = "";
                if (this._controlInfo.additionalInfo.implementationClass)
                    text += `Implementation class: ${this._controlInfo.additionalInfo.implementationClass}`;
                if (this._controlInfo.additionalInfo.dataAttributes) {
                    text += "\r\n<b>Data attributes:</b>";
                    Object.keys(this._controlInfo.additionalInfo.dataAttributes).forEach(key => {
                        text += `\r\n\u2022 ${key}: ${this._controlInfo.additionalInfo.dataAttributes[key]}`;
                    });
                }
                if (this._controlInfo.additionalInfo.rights) {
                    const evalRights = this._controlInfo.additionalInfo.rights.evaluatedRights;
                    const rights = this._controlInfo.additionalInfo.rights.rights;
                    const usedKeys: string[] = [];
                    text += "\r\n<b>Rights:</b>";
                    for (const key in evalRights) {
                        if (Reflect.get(evalRights, key) !== undefined) {
                            text += `\r\n\u2022 ${key}: ${Reflect.get(evalRights, key)}`;
                            if (Object.prototype.hasOwnProperty.call(rights, key) && Reflect.get(rights, key) !== undefined && typeof Reflect.get(rights, key) !== "boolean")
                                text += ` ${JSON.stringify(Reflect.get(rights, key))}`;
                            usedKeys.push(key);
                        }
                    }
                    for (const key in rights) {
                        if (usedKeys.indexOf(key) < 0 && Reflect.get(rights, key) !== undefined)
                            text += `\r\n\u2022 ${key}: ${JSON.stringify(Reflect.get(rights, key))}`;
                    }
                }
                if (this._controlInfo.additionalInfo.frameContext) {
                    text += "\r\n<b>FrameContext:</b>";
                    text += `\r\n\u2022 roles: ${JSON.stringify(this._controlInfo.additionalInfo.frameContext?.roles)}`;
                    text += `\r\n\u2022 transactionContextId: ${JSON.stringify(this._controlInfo.additionalInfo.frameContext?.getTransactionContextId())}`;
                }

                additionalInfo.appendChild(createElement({ type: "div", html: text }));
                this._element.appendChild(additionalInfo);
            }
        }

        return this._element;
    }

    private _addRow(title: string, value: string, rowClass: string, parent: HTMLElement): void {
        const row = createElement({ type: "tr", classes: [rowClass, bemClass.element("row")] });
        row.appendChild(createElement({ type: "th", html: title, classes: bemClass.element("row-title") }));
        row.appendChild(createElement({ type: "td", html: value, classes: bemClass.element("row-value") }));
        parent.appendChild(row);
    }
}

export interface IControlInfoObject {
    /** Název třídy */
    readonly className: string;
    /** Název atributu */
    readonly attributeName?: string;
    /** Datový typ */
    readonly dataType?: OWDataType;
    /** Doplňující název datového typu. Zobrazuje se za datovým typem (např.: Enum|nazev_enumu, OWObject|nazev_tridy). */
    readonly additionalTypeName?: string;
    /** Lokalizační klíč */
    readonly locKey: string;
    /** Aktuální jazyk */
    readonly language: string;
    /** Další informace, které se zobrazují ve vývojářském módu: data-atributy, aktuální vyhodnocení práv a třída komponenty. */
    readonly additionalInfo?: IAdditionalControlInfo;
}

export interface IAdditionalControlInfo {
    dataAttributes: IDataAttributes;
    rights?: { evaluatedRights: IHasRights; rights: IItemRightsCollection } | undefined;
    frameContext?: IFrameContext | undefined;
    implementationClass: string;
}

export interface IDataAttributes {
    [index: string]: string;
}

export class IControlInfoObject implements IControlInfoObject {
}
